import { initializeVehicleCatalog } from '@vador/library';
import { Component } from '@theme/component';

// Years describe the rendering, rather than every year a model name existed.
function renderingYears(value = '') {
  const years = new Set();
  for (const entry of value.split(/[,|]/)) {
    const match = entry.trim().replace(/[–—]/g, '-').match(/^(\d{4})(?:\s*-\s*(\d{4}))?$/);
    if (!match) continue;
    const start = Number(match[1]), end = Number(match[2] || match[1]);
    if (start < 1886 || end > 2100 || end < start) continue;
    for (let year = start; year <= end; year++) years.add(String(year));
  }
  return [...years].sort((a,b)=>Number(b)-Number(a));
}

class VadorAutomotive extends Component {
  connectedCallback() { super.connectedCallback(); this.start(); }
  updatedCallback() { super.updatedCallback(); this.start(); }
  async start() {
    this.catalogCleanup?.();
    if (this.dataset.catalogIndex) { this.catalogCleanup = await initializeVehicleCatalog(this); return; }
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const on = (target, event, fn) => target?.addEventListener(event, fn, {signal});
    const refinements = this.querySelector('[data-directory-refine]');
    if (refinements) {
      const mobile = matchMedia('(max-width: 620px)');
      const arrangeRefinements = () => { refinements.open = !mobile.matches; };
      arrangeRefinements();
      on(mobile, 'change', arrangeRefinements);
    }
    const filters = this.querySelector('[data-design-filters]');
    const status = this.querySelector('[data-design-count]');
    const cards = [...this.querySelectorAll('[data-design-group]')];
    const controls = this.querySelector('[data-directory-controls]');
    const search = this.querySelector('[data-brand-search]');
    const make = this.querySelector('[data-vehicle-make]');
    const model = this.querySelector('[data-vehicle-model]');
    const year = this.querySelector('[data-vehicle-year]');
    const body = this.querySelector('[data-vehicle-body]');
    const pager = this.querySelector('[data-design-pagination]');
    const previous = this.querySelector('[data-design-previous]');
    const next = this.querySelector('[data-design-next]');
    const pageLabel = this.querySelector('[data-design-page]');
    const cardYears = new Map(cards.map(card=>[card,renderingYears(card.dataset.designYears)]));
    const part = this.querySelector('[data-design-part]');
    const note = this.querySelector('[data-directory-note]');
    const retry = this.querySelector('[data-directory-retry]');
    const empty = this.querySelector('[data-directory-empty]');
    const strings = JSON.parse(this.querySelector('[data-directory-strings]')?.textContent || '{}');
    if (!status) return;
    let group = 'all', makes = [], directoryReady = false;
    const initialRoute = new URL(location.href);
    const normalize = value => value.trim().toLocaleLowerCase();
    const selection = () => makes.find(item => String(item.id) === make?.value);
    const modelMatches = card => !model?.value || (card.dataset.designModel || '').split('|').some(name=>normalize(name)===normalize(model.value));
    let page = 1;
    const pageSize = 12;
    const update = () => {
      let count = 0;
      const matched = [];
      const chosen = selection();
      for (const card of cards) {
        const makeMatch = !chosen || normalize(card.dataset.designMake || '') === normalize(chosen.name);
        const modelMatch = modelMatches(card);
        const bodyMatch = !body?.value || card.dataset.designBody === body.value;
        const partMatch = !part?.value || card.dataset.designPart === part.value;
        const yearMatch = !year?.value || cardYears.get(card).includes(year.value);
        const matches = (group === 'all' || card.dataset.designGroup === group) && makeMatch && modelMatch && bodyMatch && partMatch && yearMatch;
        card.hidden = true;
        if (matches) matched.push(card);
      }
      for (const button of filters?.querySelectorAll('button') || []) {
        button.setAttribute('aria-pressed', String(button.dataset.designFilter === group));
      }
      count = matched.length;
      const pages = Math.max(1, Math.ceil(count / pageSize));
      page = Math.min(page, pages);
      const start = (page - 1) * pageSize;
      for (const card of matched.slice(start, start + pageSize)) {
        card.hidden = false;
        for (const image of (!make || directoryReady) ? card.querySelectorAll('img[data-design-src]') : []) {
          image.srcset = image.dataset.designSrcset;
          image.src = image.dataset.designSrc;
          image.removeAttribute('data-design-src');
        }
      }
      status.textContent = strings.showing ? strings.showing.replace('[first]',count ? String(start + 1) : '0').replace('[last]',String(Math.min(start+pageSize,count))).replace('[count]',String(count)) : status.dataset.template.replace('[count]',String(count));
      if (pager) pager.hidden = pages <= 1;
      if (previous) previous.disabled = page <= 1;
      if (next) next.disabled = page >= pages;
      if (pageLabel) pageLabel.textContent = strings.page?.replace('[page]',String(page)).replace('[pages]',String(pages)) || '';
      if (empty) empty.hidden = count > 0;
      if (make && directoryReady) {
        const route = new URL(location.href);
        if (chosen) route.searchParams.set('make', String(chosen.id));
        else route.searchParams.delete('make');
        if (model?.value) route.searchParams.set('model', model.value);
        else route.searchParams.delete('model');
        if (body?.value) route.searchParams.set('body', body.value);
        else route.searchParams.delete('body');
        if (page > 1) route.searchParams.set('p', String(page));
        else route.searchParams.delete('p');
        if (year?.value) route.searchParams.set('year', year.value);
        else route.searchParams.delete('year');
        history.replaceState(history.state, '', route);
        for (const link of this.querySelectorAll('[data-design-group] .auto-shop__card-link')) {
          const target = new URL(link.href, location.href);
          if (chosen) target.searchParams.set('make', String(chosen.id));
          else target.searchParams.delete('make');
          if (model?.value) target.searchParams.set('model', model.value);
          else target.searchParams.delete('model');
          if (body?.value) target.searchParams.set('body', body.value);
          else target.searchParams.delete('body');
          if (page > 1) target.searchParams.set('p', String(page));
          else target.searchParams.delete('p');
          if (year?.value) target.searchParams.set('year', year.value);
          else target.searchParams.delete('year');
          link.href = target.href;
        }
      }
      for (const link of this.querySelectorAll('[data-directory-request]')) {
        const url = new URL(link.href, location.href);
        const vehicle = [year?.value, chosen?.name, model?.value, body?.value].filter(Boolean).join(' ');
        if (vehicle) url.searchParams.set('vehicle', vehicle);
        else url.searchParams.delete('vehicle');
        if (chosen) url.searchParams.set('make', String(chosen.id));
        else url.searchParams.delete('make');
        if (model?.value) url.searchParams.set('model', model.value);
        else url.searchParams.delete('model');
        if (body?.value) url.searchParams.set('body', body.value);
        else url.searchParams.delete('body');
        if (page > 1) url.searchParams.set('p', String(page));
        else url.searchParams.delete('p');
        if (year?.value) url.searchParams.set('year', year.value);
        else url.searchParams.delete('year');
        link.href = url.href;
      }
    };
    const setOptions = (select, choices, placeholder) => {
      select.replaceChildren(new Option(placeholder, ''), ...choices.map(choice => new Option(choice.label, choice.value)));
    };
    const updateYears = () => {
      if (!year) return;
      const chosen=selection(), previous=year.value;
      const values=chosen && model?.value ? [...new Set(cards.filter(card=>
        normalize(card.dataset.designMake || '') === normalize(chosen.name) &&
        modelMatches(card) && (!body?.value || card.dataset.designBody === body.value)
      ).flatMap(card=>cardYears.get(card)))].sort((a,b)=>Number(b)-Number(a)) : [];
      setOptions(year,values.map(value=>({label:value,value})),!chosen || !model?.value ? strings.choose_model : values.length ? strings.all_years : strings.no_years);
      if(values.includes(previous))year.value=previous;
      year.disabled=!values.length;
    };
    const updateBodies = () => {
      if (!body) return;
      const chosen=selection();
      const values=chosen ? [...new Set(cards.filter(card=>normalize(card.dataset.designMake || '')===normalize(chosen.name) && modelMatches(card)).map(card=>card.dataset.designBody).filter(Boolean))].sort() : [];
      setOptions(body,values.map(value=>({label:value,value})),strings.all_bodies);
      body.disabled=!values.length;
    };
    const updateModels = () => {
      if (!model) return;
      const chosen = selection();
      setOptions(model, (chosen?.models || []).map(name=>({label:name,value:name})), chosen ? strings.all_models : strings.choose_make);
      model.disabled = !chosen;
      updateBodies();
      updateYears();
    };
    const showMakes = () => {
      const previous = make.value;
      const matched = makes.filter(item=>normalize(item.name).includes(normalize(search.value)));
      setOptions(make, matched.map(item=>({label:item.name,value:String(item.id)})), matched.length ? strings.all_makes : strings.no_match);
      if (matched.some(item=>String(item.id)===previous)) make.value=previous;
      if (make.value !== previous) updateModels();
      note.textContent = matched.length ? strings.finder_note : strings.no_match;
      page=1;
      update();
    };
    if (filters) filters.hidden = false;
    if (controls) controls.hidden = false;
    on(filters, 'click', event => {
      const button = event.target.closest('button[data-design-filter]');
      if (button && filters.contains(button)) {
        group = button.dataset.designFilter;
        page=1;
        if (group === 'emblems' && model) { model.value = ''; if(year)year.value=''; if(body)body.value=''; updateBodies(); updateYears(); }
        update();
      }
    });
    on(part,'change',()=>{page=1;update();});
    on(make,'change',()=>{group='all';page=1;updateModels();update();});
    on(model,'change',()=>{if(model.value && group==='emblems')group='all';page=1;if(year)year.value='';updateBodies();updateYears();update();});
    on(year,'change',()=>{page=1;update();});
    on(body,'change',()=>{page=1;if(year)year.value='';updateYears();update();});
    const changePage = delta => {page+=delta;update();this.querySelector('[data-design-group]:not([hidden]) .auto-shop__card-link')?.focus();};
    on(previous,'click',()=>changePage(-1));
    on(next,'click',()=>changePage(1));
    on(search,'input',showMakes);
    for (const button of this.querySelectorAll('[data-directory-reset]')) on(button,'click',()=>{
      group='all';page=1;
      if (search) { search.value='';showMakes();make.value='';updateModels(); }
      if (part) part.value='';
      update();
    });
    const load = async () => {
      if (!make || !this.dataset.vehicleDirectory) return;
      retry.hidden = true;
      note.classList.remove('auto-finder__note--error');
      note.textContent = strings.loading;
      controls.setAttribute('aria-busy','true');
      try {
        const response=await fetch(this.dataset.vehicleDirectory,{signal});
        if(!response.ok) throw Error('Directory unavailable');
        const data=await response.json();
        if(signal.aborted) return;
        if(!Array.isArray(data.makes)||!data.makes.length) throw Error('Empty directory');
        makes=data.makes;
        make.disabled=false;search.disabled=false;
        showMakes();
        const restored = makes.find(item=>String(item.id)===initialRoute.searchParams.get('make'));
        if (restored) make.value=String(restored.id);
        updateModels();
        const restoredModel=initialRoute.searchParams.get('model');
        if (model && restored?.models.includes(restoredModel)) model.value=restoredModel;
        updateBodies();
        const restoredBody=initialRoute.searchParams.get('body');
        if(body && [...body.options].some(option=>option.value===restoredBody))body.value=restoredBody;
        updateYears();
        const restoredYear=initialRoute.searchParams.get('year');
        if(year && [...year.options].some(option=>option.value===restoredYear))year.value=restoredYear;
        if (refinements && (body?.value || year?.value || search?.value)) refinements.open=true;
        page=Math.max(1, Math.min(10000, Number.parseInt(initialRoute.searchParams.get('p'),10) || 1));
        directoryReady=true;
        update();
      } catch(error) {
        if(signal.aborted) return;
        note.textContent=strings.load_error;note.classList.add('auto-finder__note--error');retry.hidden=false;
        make.disabled=true;search.disabled=true;
      } finally {
        if(!signal.aborted)controls.removeAttribute('aria-busy');
      }
    };
    on(retry,'click',load);
    update();
    await load();
  }
  disconnectedCallback() { this.catalogCleanup?.(); this.controller?.abort(); super.disconnectedCallback(); }
}
if (!customElements.get('vador-automotive-component')) customElements.define('vador-automotive-component', VadorAutomotive);
